import { type ButtonHandle, createButton } from '../primitives/button';
import { type ModalHandle, createModal } from '../primitives/modal';
import { type SegmentedHandle, createSegmented } from '../primitives/segmented';
import { type SliderHandle, createSlider } from '../primitives/slider';
import { type ToggleHandle, createToggle } from '../primitives/toggle';

export type SettingsRow =
  | {
      type: 'slider';
      key: string;
      label: string;
      description?: string;
      min?: number;
      max?: number;
      step?: number;
    }
  | { type: 'toggle'; key: string; label: string; description?: string }
  | {
      type: 'segment';
      key: string;
      label: string;
      description?: string;
      options: Array<{ value: string; label?: string }>;
    };

export interface SettingsPanelOptions {
  title?: string;
  rows: SettingsRow[];
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  onClose?: () => void;
}

export interface SettingsPanelHandle {
  readonly el: HTMLElement;
  show(): void;
  hide(): void;
  setValue(key: string, value: unknown): void;
  destroy(): void;
}

type RowControl =
  | { kind: 'slider'; ctrl: SliderHandle }
  | { kind: 'toggle'; ctrl: ToggleHandle }
  | { kind: 'segment'; ctrl: SegmentedHandle };

export const presetRows: Record<string, SettingsRow> = {
  music: { type: 'slider', key: 'music', label: 'Music', description: 'Background music volume' },
  sfx: { type: 'slider', key: 'sfx', label: 'Sound FX', description: 'Game sound effects' },
  quality: {
    type: 'segment',
    key: 'quality',
    label: 'Quality',
    description: 'Lower if game runs slow',
    options: [
      { value: 'low', label: 'Low' },
      { value: 'mid', label: 'Mid' },
      { value: 'high', label: 'High' },
    ],
  },
  reduceMotion: {
    type: 'toggle',
    key: 'reduceMotion',
    label: 'Reduce motion',
    description: 'Less animation',
  },
  colorBlind: { type: 'toggle', key: 'colorBlind', label: 'Color-blind palette' },
};

export function createSettingsPanel(opts: SettingsPanelOptions): SettingsPanelHandle {
  const controls = new Map<string, RowControl>();
  const body = document.createElement('div');
  body.className = 'fg-settings';

  for (const row of opts.rows) {
    const rowEl = document.createElement('div');
    rowEl.className = 'fg-settings__row';

    const labelCol = document.createElement('div');
    labelCol.className = 'fg-settings__label-col';
    const labelMain = document.createElement('div');
    labelMain.className = 'fg-settings__label-main';
    labelMain.textContent = row.label;
    labelCol.appendChild(labelMain);
    if (row.description) {
      const labelSub = document.createElement('div');
      labelSub.className = 'fg-settings__label-sub';
      labelSub.textContent = row.description;
      labelCol.appendChild(labelSub);
    }
    rowEl.appendChild(labelCol);

    const ctrlCol = document.createElement('div');
    ctrlCol.className = 'fg-settings__ctrl-col';

    if (row.type === 'slider') {
      const ctrl = createSlider({
        value: Number(opts.values[row.key] ?? 0),
        ...(row.min !== undefined && { min: row.min }),
        ...(row.max !== undefined && { max: row.max }),
        ...(row.step !== undefined && { step: row.step }),
        onChange: (v) => opts.onChange(row.key, v),
      });
      ctrlCol.appendChild(ctrl.el);
      controls.set(row.key, { kind: 'slider', ctrl });
    } else if (row.type === 'toggle') {
      const ctrl = createToggle({
        value: Boolean(opts.values[row.key]),
        onChange: (v) => opts.onChange(row.key, v),
      });
      ctrlCol.appendChild(ctrl.el);
      controls.set(row.key, { kind: 'toggle', ctrl });
    } else {
      const ctrl = createSegmented({
        value: String(opts.values[row.key] ?? row.options[0]?.value ?? ''),
        options: row.options,
        onChange: (v) => opts.onChange(row.key, v),
      });
      ctrlCol.appendChild(ctrl.el);
      controls.set(row.key, { kind: 'segment', ctrl });
    }

    rowEl.appendChild(ctrlCol);
    body.appendChild(rowEl);
  }

  const doneBtn: ButtonHandle = createButton({
    variant: 'primary',
    label: 'DONE',
    onClick: () => {
      modal.hide();
      opts.onClose?.();
    },
  });

  const modal: ModalHandle = createModal({
    title: opts.title ?? 'Settings',
    body,
    actions: [doneBtn],
    ...(opts.onClose !== undefined && { onDismiss: opts.onClose }),
  });

  return {
    el: modal.el,
    show: () => modal.show(),
    hide: () => modal.hide(),
    setValue(key, value) {
      const c = controls.get(key);
      if (!c) return;
      if (c.kind === 'slider') c.ctrl.setValue(Number(value));
      else if (c.kind === 'toggle') c.ctrl.setValue(Boolean(value));
      else c.ctrl.setValue(String(value));
    },
    destroy() {
      doneBtn.destroy();
      for (const c of controls.values()) c.ctrl.destroy();
      modal.destroy();
    },
  };
}
